import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOnRectangleIcon,
  ArrowsUpDownIcon,
  BellIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  QuestionMarkCircleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { BetaBadge } from '../shared/BetaBadge';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationTypeIcon, formatTimeAgo } from '../notifications/notificationVisuals';
import { Notification } from '../../types/notifications';
import { SupportModal } from '../modals/SupportModal';
import { moreNavItems } from './navItems';

interface MoreSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Focus keert hierheen terug bij sluiten. */
  moreButtonRef: React.RefObject<HTMLButtonElement | null>;
}

/** Profiel is dezelfde route als Instellingen, onderscheiden door de hash. */
const PROFIEL_HREF = '/coach/instellingen';
const PROFIEL_HASH = '#account';

const rowClasses =
  'flex w-full items-center gap-3 rounded-2xl px-4 min-h-[48px] text-[15px] transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hockey-blue focus-visible:ring-offset-1 ' +
  'active:bg-black/5 dark:active:bg-white/10 motion-reduce:transition-none';

const ANIMATION_MS = 300;

export const MoreSheet: React.FC<MoreSheetProps> = ({ isOpen, onClose, moreButtonRef }) => {
  const { t } = useTranslation(['nav', 'common']);
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();

  /**
   * Instellingen en Profiel wijzen naar dezelfde pagina; alleen de hash verschilt. Zonder
   * die hash in de vergelijking kleurt Instellingen op terwijl je op Profiel klikte, want
   * het pad is identiek. De twee rijen delen daarom één bron van waarheid.
   */
  const profielActief = pathname === PROFIEL_HREF && hash === PROFIEL_HASH;
  const { user, logout, currentTeam, availableTeams, switchTeam } = useAuth();
  const { unreadCount, notifications, isLoading, markAsRead, markAllAsRead } = useNotifications();

  const panelRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<'menu' | 'notifications' | 'teams'>('menu');
  const [teamQuery, setTeamQuery] = useState('');
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Mount/unmount los van de zichtbaarheidsvlag, zodat de sluit-animatie kan aflopen
  // voordat het paneel uit de DOM verdwijnt.
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }

    setVisible(false);
    if (prefersReducedMotion) {
      setMounted(false);
      return;
    }
    const timer = setTimeout(() => setMounted(false), ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isOpen, prefersReducedMotion]);

  // Bij elke opening weer met het hoofdmenu beginnen, zonder oude zoekterm.
  useEffect(() => {
    if (isOpen) {
      setView('menu');
      setTeamQuery('');
    }
  }, [isOpen]);

  // Achtergrond niet laten meescrollen zolang het paneel open is.
  useEffect(() => {
    if (!mounted) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mounted]);

  /**
   * Focus naar het paneel bij openen, en terug naar de Meer-knop - maar alléén als het
   * paneel met Escape gesloten is.
   *
   * Onvoorwaardelijk terugzetten liet op iOS een blauwe ring om de Meer-knop achter nadat
   * je een tab had aangetikt. WebKit rekent een `focus()` vanuit script dan als
   * `:focus-visible`, en die ring blijft staan tot je ergens anders tikt: de knop leek
   * ingeschakeld terwijl het paneel al dicht was.
   *
   * Terugzetten is er voor wie geen muis of vinger gebruikt: die staat na het sluiten
   * anders met zijn focus op `body` en moet vanaf het begin van de pagina terugtabben.
   * Dat is precies het Escape-geval. Sluit je met een tik - naast het paneel, op een rij
   * erin, of op een tab in de balk - dan wijst de vinger zelf al aan waar je bent.
   */
  const focusTerugNaarKnop = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    panelRef.current?.focus();
    const button = moreButtonRef.current;
    return () => {
      if (!focusTerugNaarKnop.current) return;
      focusTerugNaarKnop.current = false;
      button?.focus();
    };
  }, [isOpen, moreButtonRef]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        focusTerugNaarKnop.current = true;
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      // Lichte focus-trap: houd Tab binnen het paneel zolang het modaal openstaat.
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Zelfde regel als TeamSwitcher: inactieve teams verbergen, behalve het huidige,
  // zodat een net gedeactiveerd team niet onder de gebruiker vandaan verdwijnt.
  const selectableTeams = useMemo(
    () => availableTeams.filter((team) => team.isActive !== false || team.teamId === currentTeam?.teamId),
    [availableTeams, currentTeam]
  );

  // Alleen ongelezen, nieuwste eerst - zoals de desktop-dropdown. Bewust geen
  // limiet: het paneel scrollt, en een lijst die minder toont dan de badge telt
  // is verwarrend.
  const unreadNotifications = useMemo(
    () =>
      notifications
        .filter((melding) => !melding.isRead)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [notifications]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [markAllAsRead]);

  const handleNotificationClick = useCallback(
    async (melding: Notification) => {
      if (!melding.isRead) {
        try {
          await markAsRead(melding.id);
        } catch (error) {
          console.error('Failed to mark notification as read:', error);
          // Doorgaan met navigeren, ook als het markeren mislukt.
        }
      }

      if (melding.actionUrl) {
        // Interne links via react-router, zodat de app niet volledig herlaadt.
        if (melding.actionUrl.startsWith('/')) {
          navigate(melding.actionUrl);
        } else {
          window.location.href = melding.actionUrl;
        }
      }
      onClose();
    },
    [markAsRead, navigate, onClose]
  );

  const filteredTeams = useMemo(() => {
    const query = teamQuery.trim().toLowerCase();
    if (!query) return selectableTeams;
    return selectableTeams.filter((team) =>
      `${team.name} ${team.shortName ?? ''}`.toLowerCase().includes(query)
    );
  }, [selectableTeams, teamQuery]);

  const handleTeamSelect = useCallback(
    async (teamId: number) => {
      try {
        await switchTeam(teamId);
      } catch (error) {
        console.error('Error switching team:', error);
      } finally {
        onClose();
      }
    },
    [switchTeam, onClose]
  );

  if (!mounted) {
    // De SupportModal blijft bereikbaar nadat het paneel is gesloten.
    return <SupportModal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} />;
  }

  return (
    <>
      {/* Bewust geen donkere dim: het paneel is zelf grijs getint glas, en samen
          met een dim eronder werd het geheel grauw. Wel een onzichtbare vanglaag,
          zodat een tik naast het paneel het nog steeds sluit. */}
      <div aria-hidden="true" onClick={onClose} className="fixed inset-0 z-30 md:hidden" />

      <div
        id="mobile-more-sheet"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-more-sheet-title"
        tabIndex={-1}
        className={clsx(
          // z-40, onder de onderbalk (z-50): het paneel schuift van onderen omhoog en
          // kwam op gelijke hoogte met de balk over de knoppen heen, waardoor de balk
          // tijdens het openen leek te verdwijnen. Achterlangs klopt ook: in rust staat
          // het paneel toch al bóven de balk (bottom-nav-space laat er precies ruimte
          // voor), dus er valt niets te bedekken. De vanglaag staat op z-30, weer een
          // stap lager, zodat hij onder het paneel blijft.
          'fixed z-40 md:hidden inset-x-2 bottom-nav-space glass-surface rounded-[28px] ring-1 ring-black/5 dark:ring-white/10',
          'max-h-[70vh] overflow-y-auto overscroll-contain pb-2 outline-none',
          'transition-transform duration-300 ease-out motion-reduce:transition-none',
          visible ? 'translate-y-0' : 'translate-y-[120%]'
        )}
      >
        <h2 id="mobile-more-sheet-title" className="sr-only">
          {t('nav:meer')}
        </h2>
        <div className="mx-auto my-2 h-1 w-9 rounded-full bg-black/15 dark:bg-white/20" />

        {view === 'notifications' ? (
          <div className="px-2 pb-1">
            <div className="flex items-center gap-2 px-2 pb-2">
              <button
                type="button"
                onClick={() => setView('menu')}
                aria-label={t('backToMenu')}
                className="p-2 -ml-2 rounded-full text-gray-500 hover:bg-black/5 dark:hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hockey-blue"
              >
                <ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
              </button>
              <span className="flex-1 text-sm font-medium text-hockey-brown">{t('notifications')}</span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  className="px-3 py-1.5 text-sm font-medium text-hockey-brown rounded-full hover:bg-black/5 dark:hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hockey-blue"
                >
                  {t('allRead')}
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-hockey-blue" aria-hidden="true" />
                {t('common:state.loading')}
              </div>
            ) : unreadNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <BellIcon className="w-10 h-10 text-gray-300 mb-2" aria-hidden="true" />
                <p className="text-sm text-gray-500">{t('noNotifications')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('allCaughtUp')}</p>
              </div>
            ) : (
              unreadNotifications.map((melding) => (
                <button
                  key={melding.id}
                  type="button"
                  onClick={() => handleNotificationClick(melding)}
                  className={clsx(rowClasses, 'items-start gap-3 py-3 text-left')}
                >
                  <NotificationTypeIcon type={melding.type} />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="flex-1 text-sm font-medium text-hockey-brown truncate">{melding.title}</span>
                      <span className="w-2 h-2 rounded-full bg-hockey-blue shrink-0" aria-label={t('unread')} />
                    </span>
                    <span className="block text-sm text-gray-700 mt-0.5">{melding.message}</span>
                    <span className="block text-xs text-gray-400 mt-1">{formatTimeAgo(melding.createdAt)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        ) : view === 'teams' ? (
          <div className="px-2 pb-1">
            <div className="flex items-center gap-2 px-2 pb-2">
              <button
                type="button"
                onClick={() => setView('menu')}
                aria-label={t('backToMenu')}
                className="p-2 -ml-2 rounded-full text-gray-500 hover:bg-black/5 dark:hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hockey-blue"
              >
                <ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
              </button>
              <span className="text-sm font-medium text-hockey-brown">{t('switchTeam')}</span>
            </div>

            {/* Zoeken pas tonen als de lijst lang genoeg is om te scrollen. */}
            {selectableTeams.length > 6 && (
              <div className="px-2 pb-2">
                <input
                  type="search"
                  value={teamQuery}
                  onChange={(event) => setTeamQuery(event.target.value)}
                  placeholder={t('searchTeamPlaceholder')}
                  aria-label={t('searchTeam')}
                  className="w-full px-3 py-2 text-[15px] bg-white/80 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-hockey-blue"
                />
              </div>
            )}

            {filteredTeams.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">{t('noTeamFound')}</p>
            ) : (
              filteredTeams.map((team) => {
                const isCurrent = team.teamId === currentTeam?.teamId;
                return (
                  <button
                    key={team.teamId}
                    type="button"
                    onClick={() => handleTeamSelect(team.teamId)}
                    aria-current={isCurrent ? 'true' : undefined}
                    // Actief team in bruin, niet in hockey-blue: dat lichte blauw
                    // haalt als tekstkleur te weinig contrast op een lichte ondergrond.
                    // Het blauw zit in het vinkje, net als in de TeamSwitcher.
                    className={clsx(
                      rowClasses,
                      'justify-between py-2',
                      isCurrent ? 'text-hockey-brown font-medium' : 'text-gray-800'
                    )}
                  >
                    <span className="flex flex-col items-start min-w-0">
                      <span className="truncate">{team.name}</span>
                      {team.shortName && <span className="text-xs text-gray-500 truncate">{team.shortName}</span>}
                    </span>
                    {/* Gevulde cirkel i.p.v. een kaal vinkje: hockey-blue als lijnkleur
                        is op deze lichte ondergrond nauwelijks te zien. Blauw als
                        vulling met wit erin, net als de actieve tab in de balk. */}
                    {isCurrent && (
                      <span
                        aria-hidden="true"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hockey-blue"
                      >
                        <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        ) : (
          <div className="px-2 pb-1">
            {/* Gebruikerskop */}
            <div className="flex items-center gap-3 px-2 pb-3 pt-1">
              <div className="w-10 h-10 bg-hockey-blue rounded-full flex items-center justify-center shrink-0">
                <span className="text-white font-semibold">
                  {user?.firstName ? user.firstName.charAt(0).toUpperCase() : 'C'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-hockey-brown truncate">
                  {user ? `${user.firstName} ${user.lastName}` : t('coachFallback')}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : t('coachFallback')}
                </p>
              </div>
            </div>

            <div className="border-t border-black/5 pt-1">
              {moreNavItems.map((item) => {
                const isActive =
                  (pathname === item.href || pathname.startsWith(`${item.href}/`)) &&
                  !(item.href === PROFIEL_HREF && profielActief);

                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={onClose}
                    aria-current={isActive ? 'page' : undefined}
                    className={clsx(
                      rowClasses,
                      // Zelfde actieve behandeling als de sidebar: blauwe vlakvulling
                      // op lage dekking met bruine tekst.
                      isActive ? 'bg-merk-tint text-hockey-brown font-medium' : 'text-gray-800'
                    )}
                  >
                    <item.icon
                      className={clsx('w-5 h-5 shrink-0', isActive ? 'text-hockey-blue' : 'text-gray-400')}
                      aria-hidden="true"
                    />
                    <span>{t(`nav:${item.id}`)}</span>
                    {item.beta && <BetaBadge />}
                  </Link>
                );
              })}

              <button type="button" onClick={() => setView('notifications')} className={clsx(rowClasses, 'text-gray-800')}>
                <BellIcon className="w-5 h-5 shrink-0 text-gray-400" aria-hidden="true" />
                <span className="flex-1 text-left">{t('notifications')}</span>
                {unreadCount > 0 && (
                  <span
                    aria-label={`${unreadCount} ongelezen meldingen`}
                    className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
                <ChevronRightIcon className="w-4 h-4 shrink-0 text-gray-400" aria-hidden="true" />
              </button>

              {/* Instellingen staat hierboven al als route; deze rij is de expliciete
                  profiel-ingang die de gebruiker verwacht. Via react-router i.p.v.
                  window.location, zodat de app niet volledig herlaadt. De hash laat de
                  pagina naar de accountkaart springen, zodat de twee rijen niet allebei
                  op hetzelfde beeld uitkomen. */}
              <button
                type="button"
                onClick={() => {
                  navigate('/coach/instellingen#account');
                  onClose();
                }}
                aria-current={profielActief ? 'page' : undefined}
                className={clsx(
                  rowClasses,
                  profielActief ? 'bg-merk-tint text-hockey-brown font-medium' : 'text-gray-800'
                )}
              >
                <UserIcon
                  className={clsx('w-5 h-5 shrink-0', profielActief ? 'text-hockey-blue' : 'text-gray-400')}
                  aria-hidden="true"
                />
                <span>{t('profile')}</span>
              </button>
            </div>

            {/* Eén rij i.p.v. de volledige teamlijst: iemand kan tien teams hebben en
                dan zou de lijst het hele paneel vullen. De lijst zit achter een
                doorklik, net als Meldingen. */}
            {selectableTeams.length > 1 && (
              <div className="border-t border-black/5 mt-1 pt-1">
                <button
                  type="button"
                  onClick={() => setView('teams')}
                  className={clsx(rowClasses, 'text-gray-800')}
                >
                  <ArrowsUpDownIcon className="w-5 h-5 shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="flex-1 text-left">{t('switchTeam')}</span>
                  <ChevronRightIcon className="w-4 h-4 shrink-0 text-gray-400" aria-hidden="true" />
                </button>
              </div>
            )}

            <div className="border-t border-black/5 mt-1 pt-1">
              <button
                type="button"
                onClick={() => {
                  setIsSupportOpen(true);
                  onClose();
                }}
                className={clsx(rowClasses, 'text-gray-800')}
              >
                <QuestionMarkCircleIcon className="w-5 h-5 shrink-0 text-gray-400" aria-hidden="true" />
                <span>{t('help')}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  logout();
                  onClose();
                }}
                className={clsx(rowClasses, 'text-red-600')}
              >
                <ArrowLeftOnRectangleIcon className="w-5 h-5 shrink-0" aria-hidden="true" />
                <span>{t('logout')}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <SupportModal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} />
    </>
  );
};
